/** A point in the body, as child indices from `<body>` down plus an offset. */
export interface NodePoint {
  path: number[];
  offset: number;
}

export interface Snapshot {
  html: string;
  anchor?: NodePoint;
  focus?: NodePoint;
}

function pathOf(root: Node, node: Node): number[] | undefined {
  const path: number[] = [];
  let current: Node | null = node;
  while (current && current !== root) {
    const parent: Node | null = current.parentNode;
    if (!parent) return undefined;
    path.unshift(Array.prototype.indexOf.call(parent.childNodes, current));
    current = parent;
  }
  return current === root ? path : undefined;
}

function nodeAt(root: Node, path: readonly number[]): Node | undefined {
  let node: Node | undefined = root;
  for (const index of path) {
    node = node?.childNodes[index];
    if (!node) return undefined;
  }
  return node;
}

export function pointOf(root: Node, node: Node | null, offset: number): NodePoint | undefined {
  if (!node) return undefined;
  const path = pathOf(root, node);
  return path ? { path, offset } : undefined;
}

/** `html` overrides the body's markup, for a caller that strips its own marks first. */
export function takeSnapshot(body: HTMLElement, html = body.innerHTML): Snapshot {
  const selection = body.ownerDocument.getSelection();
  return {
    html,
    anchor: selection ? pointOf(body, selection.anchorNode, selection.anchorOffset) : undefined,
    focus: selection ? pointOf(body, selection.focusNode, selection.focusOffset) : undefined,
  };
}

/** Puts a snapshot back, caret or selection included where it still resolves. */
export function restoreSnapshot(body: HTMLElement, snapshot: Snapshot): void {
  body.innerHTML = snapshot.html;
  const selection = body.ownerDocument.getSelection();
  if (!selection || !snapshot.anchor) return;
  const anchor = nodeAt(body, snapshot.anchor.path);
  const focus = snapshot.focus ? nodeAt(body, snapshot.focus.path) : anchor;
  if (!anchor || !focus) return;
  const clamp = (node: Node, offset: number) =>
    Math.min(
      offset,
      node.nodeType === Node.TEXT_NODE ? (node.textContent ?? '').length : node.childNodes.length
    );
  try {
    selection.setBaseAndExtent(
      anchor,
      clamp(anchor, snapshot.anchor.offset),
      focus,
      clamp(focus, snapshot.focus?.offset ?? snapshot.anchor.offset)
    );
  } catch {
    // A caret that no longer resolves is dropped, the content is what counts.
  }
}

const LIMIT = 200;
/** Typing within this many ms of the last keystroke joins its undo step. */
const TYPING_WINDOW = 1000;

/**
 * Undo/redo by snapshots of the body. The browser's own stack is not used:
 * it forgets everything as soon as the content is set from outside, and it
 * does not see the editor's own DOM operations (tables, pictures, quotes).
 */
export class EditHistory {
  private undoStack: Snapshot[] = [];
  private redoStack: Snapshot[] = [];
  /** The state after the last recorded change. */
  private current: Snapshot | undefined;
  private lastTyping = 0;

  reset(snapshot: Snapshot): void {
    this.undoStack = [];
    this.redoStack = [];
    this.current = snapshot;
    this.lastTyping = 0;
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /**
   * Records that the content became `next`. Consecutive typing joins one
   * step, the way a word processor undoes a typed phrase at once.
   */
  record(next: Snapshot, typing = false, now = Date.now()): void {
    if (!this.current) {
      this.current = next;
      return;
    }
    if (next.html === this.current.html) {
      this.current = next;
      return;
    }
    const joins =
      typing &&
      this.lastTyping > 0 &&
      now - this.lastTyping < TYPING_WINDOW &&
      this.undoStack.length > 0;
    if (!joins) {
      this.undoStack.push(this.current);
      if (this.undoStack.length > LIMIT) this.undoStack.shift();
    }
    this.redoStack = [];
    this.current = next;
    this.lastTyping = typing ? now : 0;
  }

  /**
   * The selection of the present content, taken right before an edit: the
   * undo step that edit pushes then restores the caret the user had, not the
   * one of the last input. Only for unchanged markup — nothing is recorded. A
   * caret the code placed itself before the edit (`appendHTML`, a drop point)
   * is noted too, so undo puts the caret back there.
   */
  noteSelection(present: Snapshot): void {
    if (this.current?.html === present.html) this.current = present;
  }

  undo(present: Snapshot): Snapshot | undefined {
    const previous = this.undoStack.pop();
    if (!previous) return undefined;
    this.redoStack.push(present);
    this.current = previous;
    this.lastTyping = 0;
    return previous;
  }

  redo(present: Snapshot): Snapshot | undefined {
    const next = this.redoStack.pop();
    if (!next) return undefined;
    this.undoStack.push(present);
    this.current = next;
    this.lastTyping = 0;
    return next;
  }
}
